import { IComponentOptions, IPromise, IQService, IRootScopeService, IScope, copy, equals, noop } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { StateParams, StateService } from "@uirouter/angularjs";
import colors from "wwwroot/json/colors.json";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { GtmhubController } from "@gtmhub/core";
import dayjs from "@gtmhub/dayjs";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IDynamicSubteamsRole, RoleService } from "@gtmhub/roles";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { PlanningSessionService } from "@gtmhub/sessions/services/planning-session.service";
import { ICheckAccessFactory } from "@gtmhub/shared/factories/check-permissions";
import { IAccessChecked } from "@gtmhub/shared/models";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { PermissionsService } from "@gtmhub/users";
import { IdMap } from "@gtmhub/util";
import { Flavor, recommendation } from "@webapp/analytics/models/analytics.model";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { ICollection } from "@webapp/core/core.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import {
  CadenceType,
  IAccess,
  IApiPermission,
  IApprovalWorkflowValidation,
  IApprovalWorkflowValidationItemIdMap,
  IPlanningSessionConflict,
  ISessionSettings,
  PlanningSessionStatus,
  Session,
} from "@webapp/sessions/models/sessions.model";
import { IGroupedSelectedPeople } from "@webapp/shared/components/people-selector/models/models";
import { ApprovalWorkflowSelectionModal } from "../approval-workflow-selection-form/approval-workflow-selection-form.component";

interface ISessionFormComponentRedux {
  sessions: IdMap<Session>;
  sessionsFetched: boolean;
  sessionsError: IUIError;
}

type ApprovalWorkflowEditableFields = "all-fields" | "selected-fields";

const bindStateToCtrl = (state: ISessionsStoreState): ISessionFormComponentRedux => {
  const { map: sessions, isFetched: sessionsFetched, error: sessionsError } = state.sessions;

  return {
    sessions,
    sessionsFetched,
    sessionsError,
  };
};

interface ISessionFormScope extends IScope, IExtendedRootScopeService {}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SessionFormCtrl extends ISessionFormComponentRedux {}
export class SessionFormCtrl extends GtmhubController {
  planningSession: Session;
  sessionSettings: ISessionSettings;
  permissions: { inheritsPermissions: boolean };
  indicators: {
    saving?: IIndicator;
    loading?: IIndicator;
  };
  conflicts: ICollection<IPlanningSessionConflict>;
  model = {
    dateRange: {
      startDate: dayjs().toISOString(),
      endDate: dayjs().add(1, "month").toISOString(),
    },
  };

  colors = colors.general;
  isApprovalWorkflowOpened = false;
  isSelectedFieldsChecked = false;
  sessionWorkflowSet: boolean;
  canEditSelfApproveField: boolean;
  preventOwnersFromSelfApprove: boolean;
  selectedApprovalIds: string[] = [];
  problematicApprovalIds: string[] = [];
  approvalWorkflowIndicators: {
    error?: string;
    warning?: string;
  };
  permissionsHelpArticleUrl = "https://help.quantive.com/en/articles/1401133-understand-and-set-granular-permissions-to-sessions";
  private defaultAccess: IAccess;
  private methodologyPerSessionEnabled: boolean;
  private operationType: "create" | "edit";
  private originalPlanningSession: Session;

  private initialSessionDates = {
    start: this.model.dateRange.startDate,
    end: this.model.dateRange.endDate,
  };

  private approvalWorkflowEditableFieldsTemporaryData: {
    editableFieldsInitial: ApprovalWorkflowEditableFields;
    editableFieldsCurrent: ApprovalWorkflowEditableFields;
    okr: string[];
    kr: string[];
  };

  static $inject = [
    "$ngRedux",
    "PlanningSessionsActions",
    "$rootScope",
    "$scope",
    "$state",
    "PlanningSessionService",
    "CheckAccess",
    "$uibModal",
    "AnalyticsService",
    "PermissionsService",
    "$stateParams",
    "$q",
    "RoleService",
    "FeatureTogglesFacade",
    "ApprovalWorkflowSelectionModal",
    "AccountService",
  ];

  constructor(
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private $rootScope: IRootScopeService,
    private $scope: ISessionFormScope,
    private $state: StateService,
    private planningSessionService: PlanningSessionService,
    private checkAccess: ICheckAccessFactory,
    private $uibModal: IModalService,
    private analyticsService: AnalyticsService,
    private permissionService: PermissionsService,
    private $stateParams: StateParams,
    private $q: IQService,
    private roleService: RoleService,
    private featureService: FeatureTogglesFacade,
    private approvalWorkflowSelectionModal: ApprovalWorkflowSelectionModal,
    private accountService: AccountService
  ) {
    super();

    this.operationType = this.$stateParams.id ? "edit" : "create";

    this.approvalWorkflowEditableFieldsTemporaryData = {
      editableFieldsInitial: null,
      editableFieldsCurrent: null,
      okr: [],
      kr: [],
    };

    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(planningSessionsActions.getSessionsIfMissing());
  }

  $onInit(): void {
    this.analyticsService.track("Session Form Opened", { type: this.operationType });

    this.$scope.$on("sessionSelected", (evt, sessionId: string): void => {
      this.planningSession.parentId = sessionId;
    });

    this.indicators = {};
    this.triggerIndicator("loading", "start");
    this.initFormWhenSessionsAreFetched();

    this.methodologyPerSessionEnabled = this.accountService.getAccountSetting<boolean>("methodologyPerSessionEnabled");
  }

  get isSessionMethodologyVisible(): boolean {
    return !!(this.methodologyPerSessionEnabled || this.planningSession.settings);
  }

  inheritsPermissions(inherits: boolean): void {
    if (inherits) {
      this.planningSession.access = this.defaultAccess;
      if (this.operationType !== "create") {
        this.planningSession.access.inherits = true;
      }

      this.permissions.inheritsPermissions = true;
    } else {
      this.planningSession.access.inherits = false;
      this.permissions.inheritsPermissions = false;
    }

    this.validateApprovalWorkflowSettings();
  }

  formatWeekday(weekday: string): string {
    const localEvery = localize("everyWeekday");
    const localOn = localize("onWeekday") !== "onWeekday" ? localize("onWeekday") + " " : "";

    return this.planningSession.cadenceType === "WEEKLY" ? `${localEvery} ${weekday}` : `${localOn}${weekday}`;
  }

  setCadenceStart(): void {
    if (this.planningSession.cadenceType === "MONTHLY") {
      this.planningSession.cadenceStart = {
        periodOfMonth: "firstDay",
      };
    } else {
      this.planningSession.cadenceStart = {
        weekday: "Monday",
      };
    }
  }

  setCadenceDay(): void {
    this.planningSession.cadenceStart.dayOfMonth = 1;
  }

  onPermissionsUpdated = (): void => {
    this.validateApprovalWorkflowSettings();
  };

  changeWorkflow = (reviewers: number): void => {
    this.canEditSelfApproveField = this.shouldUserBeAbleToEditSelfApproveOkrField(reviewers);

    if (!reviewers) {
      this.planningSession.workflow = null;
      this.sessionWorkflowSet = false;
      return;
    }

    if (reviewers && this.planningSession.workflow) {
      this.planningSession.workflow.reviewers = reviewers;
      this.validateApprovalWorkflowSettings();

      return;
    }

    if (reviewers && !this.planningSession.workflow) {
      this.planningSession.workflow = {
        target: ["goal"],
        reviewers: reviewers,
        approvalSettings: {
          everyoneCanApprove: true,
        },
      };
      this.sessionWorkflowSet = true;
      this.planningSession.workflow.approvalSettings.everyoneCanApprove = true;
    }

    this.validateApprovalWorkflowSettings();
  };

  onPreventOwnersFromSelfApproveChange(): void {
    this.planningSession.isSelfApproveAllowed = !this.preventOwnersFromSelfApprove;
    this.validateApprovalWorkflowSettings();
  }

  everyoneInTheAccountChanged(): void {
    this.validateApprovalWorkflowSettings();
  }

  selectMultipleAssigneesForApproval = (selected: IGroupedSelectedPeople): void => {
    this.planningSession.workflow.approvalSettings = {
      ...this.planningSession.workflow.approvalSettings,
      usersAllowedToApprove: selected.userIds,
      teamsAllowedToApprove: selected.teamIds,
    };
    this.selectedApprovalIds = [...selected.userIds, ...selected.teamIds];

    this.validateApprovalWorkflowSettings();
  };

  private validateApprovalWorkflowSettings(): void {
    if (this.selectedApprovalIds.length == 0 && !this.planningSession.workflow?.approvalSettings?.everyoneCanApprove) {
      this.approvalWorkflowIndicators = {
        error: localize("please_select_users_or_teams_as_reviewers"),
        warning: "",
      };

      return;
    }

    if (this.planningSession.workflow.approvalSettings.everyoneCanApprove) {
      this.approvalWorkflowIndicators = {
        error: "",
        warning: "",
      };
      this.problematicApprovalIds = [];

      return;
    }

    const sessionAccessSubteamsIncluded = this.setAccessWithIncludedSubteams();
    this.planningSessionService
      .validateSessionApprovalWorkflowSettings(this.planningSession.workflow, sessionAccessSubteamsIncluded, {
        ownerCanApprove: this.planningSession.isSelfApproveAllowed,
      })
      .then((approvalSettingsValidation) => {
        if (!approvalSettingsValidation) return;

        const { usersAllowedToApprove: userIds = [], teamsAllowedToApprove: teamIds = [] } = this.planningSession.workflow.approvalSettings;
        const invalidUserIds = userIds.filter((id) => ["error", "warning"].includes(approvalSettingsValidation.items[id]?.status));
        const invalidTeamIds = teamIds.filter((id) => ["error", "warning"].includes(approvalSettingsValidation.items[id]?.status));
        this.problematicApprovalIds = [...invalidUserIds, ...invalidTeamIds];

        // show the user that there are errors occurred in the approval section
        if (!this.isApprovalWorkflowOpened) {
          this.isApprovalWorkflowOpened = approvalSettingsValidation.status !== "ok";
        }

        this.setApprovalWorkflowValidationMessages(approvalSettingsValidation, invalidUserIds, invalidTeamIds);
      });
  }

  private setApprovalWorkflowValidationMessages(approvalSettingsValidation: IApprovalWorkflowValidation, invalidUserIds: string[], invalidTeamIds: string[]) {
    const { status: validationStatus, message: validationMessage, items } = approvalSettingsValidation;
    let errorMessage = "";
    let warningMessage = "";

    if (validationStatus === "error" && validationMessage === "not enough approvers") {
      errorMessage = localize("please_select_more_users_or_teams_as_reviewers");
    } else if (validationStatus === "error" && validationMessage === "not enough valid approvers") {
      errorMessage = this.setErrorMessagesWhenNotEnoughValidIds(items, invalidUserIds, invalidTeamIds);
    } else if (validationStatus === "warning" && validationMessage === "some approvers do not have the required permissions") {
      warningMessage = localize("some_users_or_teams_dont_have_the_necessary_permissions");
    }

    this.approvalWorkflowIndicators = {
      error: errorMessage,
      warning: warningMessage,
    };
  }

  private setErrorMessagesWhenNotEnoughValidIds(items: IApprovalWorkflowValidationItemIdMap, invalidUserIds: string[], invalidTeamIds: string[]): string {
    let errorMessage = localize("not_enough_users_have_the_necessary_permissions");

    if (invalidUserIds.length === 1 && invalidTeamIds.length === 0) {
      errorMessage = localize("this_user_doesnt_have_the_necessary_permissions");
    } else if (invalidUserIds.length === 0 && invalidTeamIds.length === 1) {
      errorMessage = localize("this_team_doesnt_have_members_with_permissions");
    } else if (invalidUserIds.length > 1 && invalidTeamIds.length === 0) {
      errorMessage = localize("these_users_dont_have_the_necessary_permissions");
    } else if (invalidUserIds.length === 0 && invalidTeamIds.length > 1) {
      errorMessage = localize("these_teams_dont_have_members_with_permissions");
    }

    return errorMessage;
  }

  private shouldUserBeAbleToEditSelfApproveOkrField(reviewers: number): boolean {
    return this.operationType === "edit" && !this.planningSession.workflow && reviewers > 0;
  }

  checkForConflicts(): void {
    this.triggerIndicator("saving", "start");
    this.setDateRange();
    this.clearSelectedFields();

    this.planningSessionService.checkForConflictsInSession(this.planningSession, "update").then(
      (conflicts) => {
        this.conflicts = conflicts;

        if (conflicts.totalCount === 0) {
          this.editSession();
        } else {
          this.triggerIndicator("saving", "stop");
          this.openConflictsModal(conflicts);
        }
      },
      (error) => this.triggerIndicator("saving", "error", error)
    );
  }

  save(): void {
    // In case user has edited selected fields but chose All fields option we dont want to send workflow editableKrFields and editableOkrFields to BE
    this.clearSelectedFields();

    if (this.operationType === "create") {
      this.createSession();
    } else {
      this.editSession();
    }
  }

  updateSettings(settings: ISessionSettings): void {
    this.sessionSettings = settings;
    this.setSessionSettings();
  }

  clearSelectedFields(): void {
    if (!this.isSelectedFieldsChecked && this.planningSession.workflow) {
      delete this.planningSession.workflow.editableOkrFields;
      delete this.planningSession.workflow.editableKrFields;
    }
  }

  onEditableFieldsChange(args: { isSelectedFieldsChecked: boolean }): void {
    this.isSelectedFieldsChecked = args.isSelectedFieldsChecked;

    this.approvalWorkflowEditableFieldsTemporaryData = {
      ...this.approvalWorkflowEditableFieldsTemporaryData,
      editableFieldsCurrent: args.isSelectedFieldsChecked ? "selected-fields" : "all-fields",
    };

    if (args.isSelectedFieldsChecked) {
      this.planningSession.workflow.editableOkrFields = this.approvalWorkflowEditableFieldsTemporaryData.okr;
      this.planningSession.workflow.editableKrFields = this.approvalWorkflowEditableFieldsTemporaryData.kr;

      return;
    }

    this.planningSession.workflow.editableOkrFields = [];
    this.planningSession.workflow.editableKrFields = [];
  }

  private hasApprovalWorkflowEditableFieldsOptionChanged = (): boolean => {
    return this.approvalWorkflowEditableFieldsTemporaryData.editableFieldsInitial !== this.approvalWorkflowEditableFieldsTemporaryData.editableFieldsCurrent;
  };

  disableSaveBtn(): boolean {
    return (
      equals(this.planningSession, this.originalPlanningSession) &&
      dayjs(this.model.dateRange.startDate).diff(this.originalPlanningSession.start) === 0 &&
      dayjs(this.model.dateRange.endDate).diff(this.originalPlanningSession.end) === 0 &&
      !this.hasApprovalWorkflowEditableFieldsOptionChanged()
    );
  }

  private setSessionSettings(): void {
    if (this.sessionSettings) {
      this.planningSession.settings = this.sessionSettings;
    } else {
      if (this.planningSession.settings) {
        delete this.planningSession.settings;
      }
    }
  }

  private triggerIndicator(indicatorType: "saving" | "loading", action: "start" | "stop" | "error", error?: IUIError): void {
    if (action === "start") {
      this.indicators[indicatorType] = { progress: true };
    } else if (action === "stop") {
      delete this.indicators[indicatorType];
    } else {
      this.indicators[indicatorType] = { error };
    }
  }

  private initFormWhenSessionsAreFetched(): IPromise<unknown> {
    this.originalPlanningSession = {};
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.sessionsFetched,
        () => this.sessionsError
      )
      .then(() => {
        if (this.operationType === "create") {
          this.initOnCreate();
        } else {
          this.initOnEdit();
        }
      })
      .catch((error) => {
        this.triggerIndicator("loading", "error", error);
      });
  }

  private initOnCreate(): void {
    this.planningSession = {
      title: "",
      access: {
        inherits: true,
        permissions: null,
      },
      color: this.colors[Math.floor(Math.random() * this.colors.length)],
      parentId: "",
      status: PlanningSessionStatus.OPEN,
      cadenceType: "WEEKLY" as CadenceType,
      cadenceStart: {
        weekday: "Monday",
      },
      workflow: null,
      cadenceTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isSelfApproveAllowed: true,
    };

    this.permissions = { inheritsPermissions: this.planningSession.access.inherits };
    this.preventOwnersFromSelfApprove = !this.planningSession.isSelfApproveAllowed;

    this.$q.all([this.getDefaultAccessOnCreate(), this.isPermissionsSessionsAccountReadonlyFeatureAvailable()]).catch((error) => (this.indicators.loading = { error }));
  }

  private getDefaultAccessOnCreate(): IPromise<void> {
    return this.permissionService.getDefaultAccess("session").then((access) => {
      this.defaultAccess = access;
      this.planningSession.access = this.defaultAccess;
    });
  }

  private isPermissionsSessionsAccountReadonlyFeatureAvailable(): IPromise<void> {
    return this.featureService.isFeatureAvailable("permissions-sessions-account-readonly").then((permissionsFeatureEnabled) => {
      if (!permissionsFeatureEnabled) {
        this.triggerIndicator("loading", "stop");
      } else {
        this.setPermissionsForReadonlyAccount();
      }
    });
  }

  private setPermissionsForReadonlyAccount(): void {
    this.roleService.getRoles().then((roles) => {
      this.planningSession.access = this.permissionService.getDefaultAccessForReadOnlyAccount(roles);
      this.permissions.inheritsPermissions = this.planningSession.access.inherits;
      this.triggerIndicator("loading", "stop");
    });
  }

  private initOnEdit(): void {
    const session = angular.copy(this.sessions[this.$stateParams.id]);
    session.cadenceType = session.cadence.type;

    if (session.workflow) {
      session.workflow.editableOkrFields = session.workflow.editableOkrFields || [];
      session.workflow.editableKrFields = session.workflow.editableKrFields || [];
    }

    this.permissions = {
      inheritsPermissions: session.access.inherits,
    };

    this.model.dateRange = {
      startDate: session.start,
      endDate: session.end,
    };

    if (!session.color) {
      session.color = this.colors[Math.floor(Math.random() * this.colors.length)];
    }

    if (session.settings) {
      this.sessionSettings = session.settings;
    }

    this.planningSession = session;

    // If we are editing a session we want to set workflow approval selected fields options checked if session has selected fields
    this.isSelectedFieldsChecked = session.workflow?.editableKrFields?.length > 0 || session.workflow?.editableOkrFields?.length > 0;

    if (session.workflow) {
      const approvalSettings = session.workflow.approvalSettings;

      const approvalWorkflowEditableFields: ApprovalWorkflowEditableFields = this.isSelectedFieldsChecked ? "selected-fields" : "all-fields";

      this.approvalWorkflowEditableFieldsTemporaryData = {
        editableFieldsInitial: approvalWorkflowEditableFields,
        editableFieldsCurrent: approvalWorkflowEditableFields,
        okr: session.workflow.editableOkrFields,
        kr: session.workflow.editableKrFields,
      };

      if (approvalSettings?.teamsAllowedToApprove.length > 0 || approvalSettings?.usersAllowedToApprove.length > 0) {
        this.selectedApprovalIds = [...approvalSettings.usersAllowedToApprove, ...approvalSettings.teamsAllowedToApprove];
      } else {
        // default for old sessions that do not have selected reviewers
        session.workflow.approvalSettings = {
          usersAllowedToApprove: [],
          teamsAllowedToApprove: [],
          everyoneCanApprove: true,
        };
      }

      // teams or role changes may have occurred before edit of a session
      this.validateApprovalWorkflowSettings();
    }

    this.preventOwnersFromSelfApprove = !this.planningSession.isSelfApproveAllowed;
    this.originalPlanningSession = angular.copy(session);

    this.getDefaultAccessOnEdit();
    this.processDynamicSubteamsPermissions();
  }

  private getDefaultAccessOnEdit(): IPromise<void> {
    return this.permissionService.getDefaultAccessById("session", this.planningSession.id).then((access) => {
      this.defaultAccess = access;
    });
  }

  private processDynamicSubteamsPermissions() {
    const promises: IPromise<void>[] = [];
    if (this.planningSession.access.permissions) {
      this.planningSession.access.permissions.forEach((p) => {
        if (p.principalKind === "team_and_subteams") {
          promises.push(this.setCorrectValuesToPermission(p));
        }
      });
    }

    this.$q.all(promises).then(
      () => {
        this.triggerIndicator("loading", "stop");
      },
      (error) => (this.indicators.loading = { error })
    );
  }

  private setCorrectValuesToPermission(permission: IApiPermission): IPromise<void> {
    const deferred = this.$q.defer<void>();

    this.roleService
      .getRole(permission.principalId)
      .then((response: IDynamicSubteamsRole) => {
        permission.principalId = response.teamId;
        permission.principalKind = "team";
        permission.includeSubteams = true;
        deferred.resolve();
      })
      .catch((err) => {
        if (err.status === 404) {
          // Ignoring 404. It could receive 404 when team is deleted.
          deferred.resolve();
        } else {
          deferred.reject(err);
        }
      });

    return deferred.promise;
  }

  private setDateRange(): void {
    this.planningSession.start = this.model.dateRange.startDate;
    this.planningSession.end = this.model.dateRange.endDate;
  }

  private checkSessionAccess(): IPromise<void> {
    this.triggerIndicator("saving", "start");

    return this.checkAccess.checkAccess(this.planningSession.access, this.originalPlanningSession.access).then(
      (accessChecked: IAccessChecked) => {
        if (accessChecked.canSave === true) {
          this.planningSession.access = accessChecked.access;
          if (this.operationType === "edit" && !this.planningSession.access) {
            this.planningSession.access = this.defaultAccess;
          }
        }
      },
      (error) => this.triggerIndicator("saving", "error", error)
    );
  }

  private createSession(): void {
    this.checkSessionAccess().then(() => {
      this.includeSubteamsPermissions().then(
        () => {
          this.setDateRange();
          this.planningSessionService.createPlanningSession(this.planningSession).then(
            (session) => {
              this.trackRecommendationHelpfulness(session);
              this.triggerIndicator("saving", "stop");
              this.$rootScope.$broadcast("sessionCreated", session);
              this.$ngRedux.dispatch(this.planningSessionsActions.addSession(session));
              // If the user created new session from cloning session modal, he will be redirected to the modal instead of session goals

              if (this.$stateParams.isCloningOperation) {
                this.$state.go("^.^");
              } else {
                this.$state.go("gtmhub.goals.all", { planningSessionId: session.id });
              }
            },
            (error) => this.triggerIndicator("saving", "error", error)
          );
        },
        (error) => this.triggerIndicator("saving", "error", error)
      );
    });
  }

  private trackRecommendationHelpfulness(session: Session): void {
    // Tracking Session creation recommendations
    // recommendation is helpful if user doesn’t change start date
    this.analyticsService.track(recommendation, {
      is_useful: new Date(session.start).toDateString() === new Date(this.initialSessionDates.start).toDateString(),
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;start_date",
    });
    // recommendation is helpful if user doesn’t change end date
    this.analyticsService.track(recommendation, {
      is_useful: new Date(session.end).toDateString() === new Date(this.initialSessionDates.end).toDateString(),
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;end_date",
    });
    // recommendation is helpful if user doesn’t change cadence
    this.analyticsService.track(recommendation, {
      is_useful: session.cadence.type === "WEEKLY",
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;cadence",
    });
    // recommendation is helpful if user doesn’t change cadence day
    this.analyticsService.track(recommendation, {
      is_useful: session.cadenceStart.weekday === "Monday",
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;cadence_day",
    });
    // recommendation is helpful if session doesn’t have workflow
    this.analyticsService.track(recommendation, {
      is_useful: !session.workflow,
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;approval_workflow",
    });
  }

  private setAccessWithIncludedSubteams(): IAccess {
    /* Temporary fix for the aim of the session approval workflow feature,
    / because the permissions component needs to be improved.
    / Currently the webapp permissions component does not work with "team_and_subteams",
    / but the BE requires it, in order to edit the access object and change the id to be the team and sub-team role _id.
    / Also "team_and_subteams" is used when checking the permissions for certain action that requires them.
    / That's way when adding a team with option for the sub-teams before sending the request
    / the webapp is making some adjustments - changing the type to team_and_subteams, but not the id.
    */

    if (!this.planningSession.access || !this.planningSession.access?.permissions) {
      return this.planningSession.access;
    }

    const newAccess = copy(this.planningSession.access);
    newAccess.permissions = newAccess.permissions.reduce((acc, permission) => {
      if (permission.principalKind === "team" && permission.includeSubteams) {
        acc.push({
          ...permission,
          principalKind: "team_and_subteams",
        });
      }

      acc.push(permission);

      return acc;
    }, []);

    newAccess.inherits = newAccess.permissions.length > 0;

    return newAccess;
  }

  private includeSubteamsPermissions(): IPromise<void> {
    if (this.planningSession.access === null) {
      return this.$q.resolve();
    }

    const parentsWithIncludedSubteams = this.getTeamsPermissionsWithIncludedSubteams();
    if (!parentsWithIncludedSubteams.length) {
      return this.$q.resolve();
    } else {
      parentsWithIncludedSubteams.forEach((p) => (p.principalKind = "team_and_subteams"));
    }

    if (this.planningSession.access.permissions.length > 0) {
      this.planningSession.access.inherits = false;
    }

    return this.$q.resolve();
  }

  private getTeamsPermissionsWithIncludedSubteams = () => {
    return this.planningSession.access.permissions.filter((permission) => {
      return permission.principalKind === "team" && permission.includeSubteams;
    });
  };

  openApprovalWorkflowSelectionSelection(): void {
    this.approvalWorkflowSelectionModal
      .open(this.planningSession.workflow.editableOkrFields, this.planningSession.workflow.editableKrFields, this.planningSession.id)
      .then(({ unSelectedOkrFields, unSelectedKrFields }) => {
        this.planningSession.workflow.editableOkrFields = unSelectedOkrFields;
        this.planningSession.workflow.editableKrFields = unSelectedKrFields;

        this.approvalWorkflowEditableFieldsTemporaryData = {
          ...this.approvalWorkflowEditableFieldsTemporaryData,
          okr: unSelectedOkrFields,
          kr: unSelectedKrFields,
        };
      });
  }

  private editSession(): void {
    this.inheritsPermissions(this.permissions.inheritsPermissions);
    this.checkSessionAccess().then(() => {
      this.includeSubteamsPermissions().then(() => {
        this.planningSessionService.updatePlanningSession(this.planningSession).then(
          () => {
            this.triggerIndicator("saving", "stop");
            this.$state.go("^.^");
          },
          (error) => this.triggerIndicator("saving", "error", error)
        );
      });
    });
  }

  private openConflictsModal(conflicts: ICollection<IPlanningSessionConflict>): void {
    this.$uibModal
      .open({
        template: require("../../views/conflicts.html"),
        windowClass: "medium",
        controller: "PlanningSessionConflictsCtrl",
        resolve: {
          conflicts: () => conflicts,
          session: () => this.planningSession,
          onSave: () => () => {
            this.save();
          },
          ignoreAndClone: () => false,
        },
      })
      .result.catch(noop);
  }
}

export const SessionFormComponent: IComponentOptions = {
  template: require("./session-form.html"),
  controller: SessionFormCtrl,
  bindings: {},
};
